import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Slider,
  Switch,
} from "antd";
import { memo, useEffect, useState } from "react";

import { DisplayModes, RoadNetworks } from "../js/constants";
import "./Settings.css";

const Settings = memo(
  ({
    areAlertsVisible,
    setAreAlertsVisible,
    areJamsVisible,
    setAreJamsVisible,
    areIrregularitiesVisible,
    setAreIrregularitiesVisible,
    colorBy,
    setColorBy,
    roadNetwork,
    setRoadNetwork,
    maxAngleDegrees,
    setMaxAngleDegrees,
    minOverlapPercentage,
    setMinOverlapPercentage,
    minCongestionCoefficient,
    setMinCongestionCoefficient,
  }) => {
    const ColorSetting = ({ colorBy, setColorBy }) => {
      const [selectedValue, setSelectedValue] = useState(colorBy);

      useEffect(() => {
        const timeoutId = setTimeout(() => {
          setColorBy(selectedValue); // this goes upstream to the parent component
        }, 500);
        return () => clearTimeout(timeoutId);
      }, [selectedValue, setColorBy]);

      return (
        <Select
          defaultValue={selectedValue}
          onChange={setSelectedValue}
          options={Object.entries(DisplayModes).map(([idx, lbl]) => ({
            value: idx,
            label: lbl,
          }))}
        />
      );
    };

    const RoadNetworkSetting = ({ roadNetwork, setRoadNetwork }) => {
      const [selectedValue, setSelectedValue] = useState(roadNetwork);

      useEffect(() => {
        const timeoutId = setTimeout(() => {
          setRoadNetwork(selectedValue); // this goes upstream to the parent component
        }, 500);
        return () => clearTimeout(timeoutId);
      }, [selectedValue, setRoadNetwork]);

      return (
        <Select
          defaultValue={selectedValue}
          onChange={setSelectedValue}
          options={Object.entries(RoadNetworks).map(([idx, lbl]) => ({
            value: idx,
            label: lbl,
          }))}
        />
      );
    };

    const MaxAngleSetting = ({ maxAngleDegrees, setMaxAngleDegrees }) => {
      const minValue = 0;
      const maxValue = 90;

      const [inputValue, setInputValue] = useState(maxAngleDegrees);

      useEffect(() => {
        const timeoutId = setTimeout(() => {
          setMaxAngleDegrees(inputValue); // this goes upstream to the parent component
        }, 500);
        return () => clearTimeout(timeoutId);
      }, [inputValue, setMaxAngleDegrees]);

      const formatter = (value) => `${value}°`;

      return (
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Slider
              min={minValue}
              max={maxValue}
              onChange={setInputValue}
              value={typeof inputValue === "number" ? inputValue : null}
              tooltip={{
                formatter,
              }}
            />
          </Col>
          <Col span={12}>
            <InputNumber
              min={minValue}
              max={maxValue}
              value={inputValue}
              onChange={setInputValue}
              addonAfter="°"
            />
          </Col>
        </Row>
      );
    };

    const MinOverlapSetting = ({
      minOverlapPercentage,
      setMinOverlapPercentage,
    }) => {
      const minValue = 0;
      const maxValue = 100;

      const [inputValue, setInputValue] = useState(
        maxValue - minOverlapPercentage,
      );

      useEffect(() => {
        const timeoutId = setTimeout(() => {
          setMinOverlapPercentage(maxValue - inputValue); // this goes upstream to the parent component
        }, 500);
        return () => clearTimeout(timeoutId);
      }, [inputValue, setMinOverlapPercentage]);

      const formatter = (value) => `${maxValue - value}%`;

      return (
        <Row gutter={[8, 8]}>
          <Col span={12}>
            <Slider
              min={minValue}
              max={maxValue}
              onChange={setInputValue}
              value={Number.isNaN(inputValue) ? null : inputValue}
              tooltip={{
                formatter,
              }}
              reverse
            />
          </Col>
          <Col span={12}>
            <InputNumber
              min={minValue}
              max={maxValue}
              value={maxValue - inputValue}
              onChange={(v) => {
                if (v != null) {
                  setInputValue(maxValue - v);
                } else {
                  setInputValue(Number.NaN);
                }
              }}
              addonAfter="%"
            />
          </Col>
        </Row>
      );
    };

    const MinCongestionCoefficientSetting = ({
      minCongestionCoefficient,
      setMinCongestionCoefficient,
    }) => {
      const minValue = 0.0;
      const maxValue = 1.0;
      const step = 0.01;
      const precision = 2;

      const [inputValue, setInputValue] = useState(
        maxValue - minCongestionCoefficient,
      );

      useEffect(() => {
        const timeoutId = setTimeout(() => {
          setMinCongestionCoefficient(
            +(maxValue - inputValue).toFixed(precision),
          ); // this goes upstream to the parent component
        }, 500);
        return () => clearTimeout(timeoutId);
      }, [inputValue, setMinCongestionCoefficient]);

      const formatter = (value) => `${(maxValue - value).toFixed(precision)}`;

      return (
        <Row gutter={[8, 0]} align={"middle"} justify="start">
          <Col span={10}>
            <Slider
              min={minValue}
              max={maxValue}
              onChange={setInputValue}
              value={Number.isNaN(inputValue) ? null : inputValue}
              tooltip={{
                formatter,
              }}
              reverse
              step={step}
            />
          </Col>
          <Col span={9}>
            <InputNumber
              min={minValue}
              max={maxValue}
              value={maxValue - inputValue}
              onChange={(v) => {
                if (v != null) {
                  setInputValue(maxValue - v);
                } else {
                  setInputValue(Number.NaN);
                }
              }}
              step={step}
              precision={precision}
              style={{
                width: "100%",
              }}
            />
          </Col>
          <Col span={3}>
            k=
            {Number.isNaN(inputValue)
              ? ""
              : (1 / (1 - maxValue + inputValue)).toFixed(1)}
          </Col>
        </Row>
      );
    };

    const maxAngleDoc = () => {
      Modal.info({
        title: "Angle maximum",
        content: <div>...</div>,
        footer: null,
        closable: true,
        maskClosable: true,
      });
    };

    const minOverlapDoc = () => {
      Modal.info({
        title: "Recoupement minimum",
        content: <div>...</div>,
        footer: null,
        closable: true,
        maskClosable: true,
      });
    };

    const minCongestionCoefficientDoc = () => {
      Modal.info({
        title: "Coefficient de congestion minimum",
        content: <div>...</div>,
        footer: null,
        closable: true,
        maskClosable: true,
      });
    };

    const LayerSetting = ({
      areAlertsVisible,
      setAreAlertsVisible,
      areJamsVisible,
      setAreJamsVisible,
      areIrregularitiesVisible,
      setAreIrregularitiesVisible,
    }) => {
      const handleAlertsSwitchChange = () => {
        setAreAlertsVisible(!areAlertsVisible);
      };

      const handleJamsOrIrregularitiesSwitchChange = () => {
        setAreJamsVisible(!areJamsVisible);
        setAreIrregularitiesVisible(!areIrregularitiesVisible);
      };

      const data = [
        {
          key: "alerts",
          title: "Alertes",
          content: (
            <Switch
              style={{ width: "100%" }}
              value={areAlertsVisible}
              onChange={handleAlertsSwitchChange}
            />
          ),
        },
        {
          key: "jams",
          title: "Bouchons",
          content: (
            <Switch
              style={{ width: "100%" }}
              value={areJamsVisible}
              onChange={handleJamsOrIrregularitiesSwitchChange}
            />
          ),
        },
        {
          key: "irregularities",
          title: "Irrégularités",
          content: (
            <Switch
              style={{ width: "100%" }}
              value={areIrregularitiesVisible}
              onChange={handleJamsOrIrregularitiesSwitchChange}
            />
          ),
        },
      ];

      return (
        <>
          <List
            size="small"
            dataSource={data}
            rowKey="key"
            renderItem={(item) => (
              <Row span={24} justify="space-around">
                <Col span={12}>{item.title}</Col>
                <Col span={5}>{item.content}</Col>
              </Row>
            )}
          />
        </>
      );
    };

    const settingsData = [
      {
        key: "layers",
        title: "Jeux de données",
        content: (
          <LayerSetting
            areAlertsVisible={areAlertsVisible}
            setAreAlertsVisible={setAreAlertsVisible}
            areJamsVisible={areJamsVisible}
            setAreJamsVisible={setAreJamsVisible}
            areIrregularitiesVisible={areIrregularitiesVisible}
            setAreIrregularitiesVisible={setAreIrregularitiesVisible}
          />
        ),
      },
      {
        key: "road-network",
        title: "Sélection du graphe routier",
        content: (
          <RoadNetworkSetting
            roadNetwork={roadNetwork}
            setRoadNetwork={setRoadNetwork}
          />
        ),
      },
      {
        key: "color-by",
        title: "Colorier les tronçons selon le",
        content: <ColorSetting colorBy={colorBy} setColorBy={setColorBy} />,
      },
      {
        key: "min-congestion-coeff",
        title: (
          <div>
            <span style={{ marginRight: "5px" }}>Coeff. de congestion min</span>
            <Button
              size="small"
              color="black"
              variant="text"
              onClick={minCongestionCoefficientDoc}
            >
              <InfoCircleOutlined />
            </Button>
          </div>
        ),
        content: (
          <MinCongestionCoefficientSetting
            minCongestionCoefficient={minCongestionCoefficient}
            setMinCongestionCoefficient={setMinCongestionCoefficient}
          />
        ),
      },
      {
        key: "angle-max",
        title: (
          <div>
            <span style={{ marginRight: "5px" }}>Angle max</span>
            <Button
              size="small"
              color="black"
              variant="text"
              onClick={maxAngleDoc}
            >
              <InfoCircleOutlined />
            </Button>
          </div>
        ),
        content: (
          <MaxAngleSetting
            maxAngleDegrees={maxAngleDegrees}
            setMaxAngleDegrees={setMaxAngleDegrees}
          />
        ),
      },
      {
        key: "min-overlap",
        title: (
          <div>
            <span style={{ marginRight: "5px" }}>Recoupement min</span>
            <Button
              size="small"
              color="black"
              variant="text"
              onClick={minOverlapDoc}
            >
              <InfoCircleOutlined />
            </Button>
          </div>
        ),
        content: (
          <MinOverlapSetting
            minOverlapPercentage={minOverlapPercentage}
            setMinOverlapPercentage={setMinOverlapPercentage}
          />
        ),
      },
    ];

    const settingsItems = (
      <div className="settings">
        <List
          size="small"
          itemLayout="horizontal"
          dataSource={settingsData}
          rowKey="key"
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                key={item.key}
                title={item.title}
                description={item.content}
              />
            </List.Item>
          )}
        />
      </div>
    );

    return (
      <div className="settings-wrap">
        <Collapse
          size="small"
          accordion={false}
          collapsible="header"
          defaultActiveKey={["1"]}
          bordered={false}
          items={[
            {
              key: "1",
              label: <span style={{ fontSize: "16px" }}>Configuration</span>,
              children: settingsItems,
            },
          ]}
          style={{ background: "white", height: "100%" }}
        />
      </div>
    );
  },
);

export default Settings;
